import React, { useState, useCallback, useMemo, useEffect } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import moment from "moment";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useFetch } from "services/hooks/useFetch";
import {
  theme,
  defaultAllSelection,
} from "services/constants";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useExcelDownload } from "components/CustomComponents/useExcelDownload";
import { FilterProvider } from "pages/FilterContext";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import FunctionsRoundedIcon from "@mui/icons-material/FunctionsRounded";
import { checkViewerPermission, subModuleNameMatches } from "services/utils/HelperFunction";
import {
  WrkdHrsReportHeader1,
  WrkdHrsReportHeader2,
} from "pages/TeamZone/TeamZoneHeaders";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { CompactHeaderSelectFilter } from "components/CustomComponents/CompactHeaderSelectFilter";
import { WorkingHoursReport } from "./WorkingHours";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import {
  designShell,
  withDesignFont,
  StatCard,
} from "services/constants/designTokens";
import { approveStickyCustomFilterBar } from "pages/TeamZone/Approve/approveUi";

const HOURS_VIEW_OPTIONS = [
  { value: "ot_hours", label: "Payable Hours" },
  { value: "working_hours", label: "Working Hours" },
];

const sumDurationValues = (values = []) => {
  const totalMinutes = values.reduce((sum, value) => {
    const normalizedValue = `${value || ""}`.trim();
    const match = normalizedValue.match(/^(\d{1,4}):(\d{2})(?::\d{2})?$/);
    if (!match) return sum;
    return sum + Number(match[1]) * 60 + Number(match[2]);
  }, 0);

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
};

export const WrkedHrsReports = ({
  setShowReport,
  embeddedReportTypes = null,
  selectedReportType = null,
  onReportTypeChange = null,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  useEffect(() => {
    if (!customFilterNode) return undefined;
    const updateHeight = () => setCustomFilterHeight(customFilterNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  const normalizeDateValue = (date) => {
    if (!date) return "";

    if (date instanceof Date) {
      return moment(date).format("YYYY-MM-DD");
    }

    if (moment.isMoment(date)) {
      return date.format("YYYY-MM-DD");
    }

    if (typeof date === "string") {
      const parsedDate = moment(
        date,
        ["DD/MM/YYYY", "YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601],
        true,
      );

      if (parsedDate.isValid()) {
        return parsedDate.format("YYYY-MM-DD");
      }
    }

    const fallbackDate = moment(date);
    return fallbackDate.isValid() ? fallbackDate.format("YYYY-MM-DD") : "";
  };

  const { domain, userDetails, userScreenAccess, topbarCompanyName } =
    useSelector((state) => state.tokenReducer);
  const { downloadExcel, downloadPdf, downloadCsv } = useExcelDownload();
  const [departmentName, setDepartmentName] = useState("All");
  const [departmentId, setDepartmentId] = useState(defaultAllSelection);
  const [fetchDetails, setFetchDetails] = useState(true);
  const [workingHoursDetails, setWorkingHoursDetails] = useState([]);
  const [masterLeaveDataForSearch, setMasterLeaveDataForSearch] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [wrkdHrsData, setWrkdHrsData] = useState([]);
  const [hoursViewType, setHoursViewType] = useState("ot_hours");
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "today" }),
  );
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);
  const { result: departmentData } = useFetch(
    `${domain}get-department/?user_id=${userDetails.id}&company_id=${requestCompanyId}`,
  );

  const teamZoneReports = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );

  const subModuleId = teamZoneReports?.submodules?.find(
    (sub) => subModuleNameMatches(sub.sub_module_name, "Working Hours Report"),
  )?.sub_module_id;

  let check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    "Working Hours Report",
  );

  // Tagged with a distinct groupName per block so SaveViewButton's existing
  // same-group drag constraint keeps identity columns (Employee) and summary
  // columns (Summary) from being interleaved with each other — the dynamic
  // per-date block always renders physically between the two and can't
  // itself be reordered, so an interleaved save would have nowhere sensible
  // to place it on screen.
  const workingHoursFullHeaders = useMemo(
    () => [
      ...WrkdHrsReportHeader1.map((header) => ({
        ...header,
        groupName: "Employee",
      })),
      ...WrkdHrsReportHeader2.map((header) => ({
        ...header,
        groupName: "Summary",
      })),
    ],
    [],
  );

  const header1Keys = useMemo(
    () => new Set(WrkdHrsReportHeader1.map((header) => header.key)),
    [],
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  // Every other TeamZone Reports screen (DailyReport, OvertimeReport,
  // Anomalies, WorkforceReport, EsslReport, DeviceStatusReport,
  // LeaveAndPermission, WorkingHours/*) was already migrated to this one
  // shared hook — this screen was the one left behind, still hand-rolling
  // the ~200 lines useSavedViewSelector itself now encapsulates (raw
  // useSavedHeaderViews + a manually-built SavedViewSelector titleOverride
  // with its own default-view/active-view bookkeeping). Behavior is the
  // same; this just adopts the same "updated design" every sibling report
  // already has. One real consequence: the hook derives its own default-view
  // id from storageKey (`__default_view__${storageKey}`) rather than
  // accepting a caller-chosen one, so it doesn't reuse this screen's old
  // hardcoded "__working_hours_report_default_view__" id — anyone who'd
  // already customized and saved their default column set on this specific
  // screen will see it reset to all columns once, the same one-time reset
  // every other screen's own migration to this hook already went through.
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "working_hours_report_saved_views",
    fullHeaders: workingHoursFullHeaders,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });

  const newButtonComponent = null;

  const visibleHeader1 = useMemo(
    () => visibleHeaders.filter((header) => header1Keys.has(header.key)),
    [visibleHeaders, header1Keys],
  );
  const visibleHeader2 = useMemo(
    () => visibleHeaders.filter((header) => !header1Keys.has(header.key)),
    [visibleHeaders, header1Keys],
  );

  const handleStartDate = (startDate, endDate, label) => {
    setStartDate(normalizeDateValue(startDate));
    setEndDate(normalizeDateValue(endDate));
    setDateRangeLabel(label || "Custom Range");
    setFetchDetails(true);
  };

  const updateDepartmentName = (event, data) => {
    if (data && data?.label) {
      setDepartmentName(data.label);
      setDepartmentId(data.id);
      setFetchDetails(true);
    }
  };

  const departmentList = [
    {
      id: defaultAllSelection,
      label: "All",
      department_name: "All",
    },
    ...(departmentData?.map((obj) => ({
      ...obj,
      label: obj.department_name,
    })) ?? []),
  ];

  const workingHoursRows = (workingHoursDetails?.attendance_data || []).map(
    (employee) => {
      const absentDays =
        workingHoursDetails?.headers?.reduce((count, header) => {
          const dayData = employee?.[header.date];
          const attendanceStatus = dayData?.attendance_status?.toLowerCase?.();
          const responseStatus = dayData?.status_val_with_break_hours;

          if (attendanceStatus?.includes("absent") || responseStatus === "A") {
            return count + 1;
          }

          return count;
        }, 0) || 0;

      return {
        ...employee,
        absent_count: employee?.absent_count ?? absentDays,
      };
    },
  );


  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setWrkdHrsData(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const updateHoursViewType = (_, data) => {
    if (data?.value) {
      setHoursViewType(data.value);
    }
  };

  const getSelectedHoursValue = useCallback(
    (dayData) => {
      if (!dayData || typeof dayData !== "object") {
        return "-";
      }

      if (hoursViewType === "working_hours") {
        return (
          dayData.status_val_with_break_hours ||
          dayData.attendance_status ||
          "-"
        );
      }

      return (
        dayData.payable_grd_witn_ot_hours || dayData.attendance_status || "-"
      );
    },
    [hoursViewType],
  );

  const getSummaryCellValue = useCallback(
    (row, headerKey) => {
      if (headerKey === "total_duty_hours" && hoursViewType === "ot_hours") {
        return row?.total_payable_grd_shift_hours || "0";
      }

      if (headerKey === "total_duty_ot_break_hours") {
        if (hoursViewType === "ot_hours") {
          return row?.total_payable_grd_shift_ot_hours || "0";
        }

        if (hoursViewType === "working_hours") {
          return row?.total_duty_and_ot_hours || "0";
        }
      }

      return row?.[headerKey] || "0";
    },
    [hoursViewType],
  );

  const getReportMetadata = useCallback(() => {
    const formattedStartDate = moment(
      startDate,
      ["DD/MM/YYYY", "YYYY-MM-DD"],
      true,
    ).format("DD/MM/YYYY");
    const formattedEndDate = moment(
      endDate,
      ["DD/MM/YYYY", "YYYY-MM-DD"],
      true,
    ).format("DD/MM/YYYY");

    return {
      "Company Name": topbarCompanyName || "N/A",
      Department: departmentName || "All",
      "Hours Type":
        HOURS_VIEW_OPTIONS.find((option) => option.value === hoursViewType)
          ?.label || "Payable Hours",
      "Date Range": `${formattedStartDate} to ${formattedEndDate}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
      "Overall Total Count": workingHoursDetails?.attendance_data?.length || 0,
    };
  }, [
    departmentName,
    endDate,
    hoursViewType,
    startDate,
    topbarCompanyName,
    workingHoursDetails,
  ]);

  const summaryCards = useMemo(() => {
    const rows = workingHoursRows || [];

    return [
      {
        key: "employees",
        title: "Employees",
        value: rows.length,
        icon: Groups2RoundedIcon,
        accent: "#1D4ED8",
        tint: "#EEF4FF",
      },
      {
        key: "absent-days",
        title: "ABS Days",
        value: rows.reduce(
          (sum, row) => sum + Number(row?.absent_count || 0),
          0,
        ),
        icon: EventBusyRoundedIcon,
        accent: "#EF4444",
        tint: "#FEF2F2",
      },
      {
        key: "duty-hours",
        title: hoursViewType === "ot_hours" ? "Duty Hours" : "Working Hours",
        value: sumDurationValues(
          rows.map((row) => getSummaryCellValue(row, "total_duty_hours")),
        ),
        icon: ScheduleRoundedIcon,
        accent: "#0F766E",
        tint: "#E6F8F4",
      },
      {
        key: "ot-hours",
        title: "OT Hours",
        value: sumDurationValues(
          rows.map((row) => getSummaryCellValue(row, "ot_hours")),
        ),
        icon: TimerRoundedIcon,
        accent: "#F97316",
        tint: "#FFF7ED",
      },
      {
        key: "total-hours",
        title: "Total Hours",
        value: sumDurationValues(
          rows.map((row) =>
            getSummaryCellValue(row, "total_duty_ot_break_hours"),
          ),
        ),
        icon: FunctionsRoundedIcon,
        accent: "#7C3AED",
        tint: "#F7F0FF",
      },
    ];
  }, [getSummaryCellValue, hoursViewType, workingHoursRows]);

  const formatWorkingHoursValue = useCallback((value) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const normalizedValue = `${value}`.trim();
    const timeMatch = normalizedValue.match(/^(\d+):(\d{2})(?::\d{2})?$/);

    if (timeMatch) {
      return `${timeMatch[1]}:${timeMatch[2]}`;
    }

    return normalizedValue;
  }, []);

  const getWorkingHoursExportHeaders = useCallback(() => {
    const dynamicExportHeaders = (workingHoursDetails?.headers || []).map(
      (header) => ({
        key: header.date,
        name: `${header.day} - ${header.day_number}`,
        type: "string",
        customFormatter: (row) => {
          const dayData = row?.[header.date];
          if (!dayData || typeof dayData !== "object") {
            return "-";
          }

          return formatWorkingHoursValue(getSelectedHoursValue(dayData));
        },
      }),
    );

    const summaryExportHeaders = visibleHeader2.map((header) => ({
      ...header,
      ...(header.key !== "absent_count"
        ? {
            exportAsText: true,
            customFormatter: (row) =>
              formatWorkingHoursValue(getSummaryCellValue(row, header.key)),
          }
        : {}),
    }));

    return [...visibleHeader1, ...dynamicExportHeaders, ...summaryExportHeaders];
  }, [
    formatWorkingHoursValue,
    getSelectedHoursValue,
    getSummaryCellValue,
    visibleHeader1,
    visibleHeader2,
    workingHoursDetails,
  ]);

  const handleWorkingHoursDownload = useCallback(
    async (downloadRows = [], onProgress, downloadFormat = "excel") => {
      const exportRows = downloadRows?.length ? downloadRows : workingHoursRows;
      const exportHeaders = getWorkingHoursExportHeaders();

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadPdf
          : downloadFormat === "csv"
            ? downloadCsv
            : downloadExcel;

      await exportToFormat(
        exportRows,
        exportHeaders,
        "Working Hours",
        {},
        getReportMetadata(),
        [],
      );

      if (typeof onProgress === "function") {
        onProgress(100);
      }
    },
    [
      downloadCsv,
      downloadExcel,
      downloadPdf,
      getReportMetadata,
      getWorkingHoursExportHeaders,
      workingHoursRows,
    ],
  );

  return (
    <ThemeProvider theme={withDesignFont(theme)}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        {/* This screen only ever renders embedded inside TeamZone Reports'
            grouped category page (Reports/index.js) or the Working Hours
            report-type switcher (WorkingHours/index.js), both of which
            already supply designShell.pageSurface (background + min-height)
            and page padding. Re-applying pageSurface/px/py here double-
            stacked the min-height and padding on top of the parent's. */}
        <Box sx={{ pb: 0, width: "100%", maxWidth: "100%" }}>
          <Box sx={{ ...designShell.sectionCard, p: 1.5, mb: 2 }}>
            <Mui.Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center">
              <ApprovalDateRangeFilter
                thisMonthToDate
                startDate={startDate}
                endDate={endDate}
                dateRangeLabel={dateRangeLabel}
                onChange={handleStartDate}
                includeCurrentDate={false}
                showToday={false}
                sx={{ minHeight: 40, height: 40, width: { xs: "100%", sm: "auto" } }}
              />
              <CompactHeaderSelectFilter
                dataList={departmentList}
                selectedValue={departmentName}
                updateDataFunction={updateDepartmentName}
                Icons={PersonOutlineIcon}
                staticLabel="Department"
                widthSx={{ width: { xs: "100%", sm: "auto" }, minWidth: { xs: "100%", sm: 220 } }}
              />
              {embeddedReportTypes?.length && onReportTypeChange ? (
                <CompactHeaderSelectFilter
                  dataList={embeddedReportTypes}
                  selectedValue={embeddedReportTypes.find((option) => option.label === selectedReportType || option.value === selectedReportType) || null}
                  updateDataFunction={onReportTypeChange}
                  Icons={PersonOutlineIcon}
                  staticLabel="Report Type"
                  widthSx={{ width: { xs: "100%", sm: "auto" }, minWidth: { xs: "100%", sm: 260 } }}
                />
              ) : null}
              <CompactHeaderSelectFilter
                dataList={HOURS_VIEW_OPTIONS}
                selectedValue={HOURS_VIEW_OPTIONS.find((option) => option.value === hoursViewType) || HOURS_VIEW_OPTIONS[0]}
                updateDataFunction={updateHoursViewType}
                staticLabel="Hours Type"
                widthSx={{ width: { xs: "100%", sm: "auto" }, minWidth: { xs: "100%", sm: 240 } }}
              />
            </Mui.Stack>
          </Box>
          <Mui.Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 1.5,
              mb: 2,
            }}
          >
            {summaryCards.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={card.value}
                accent={card.accent}
                tint={card.tint}
              />
            ))}
          </Mui.Box>
          {/* CustomFilter's sticky toolbar sits OUTSIDE the sectionCard, as a
              sibling — matching the canonical listview-table-container
              pattern (Administration/ListView.js, Approve/OverTime/index.js).
              Nesting it inside a sectionCard with overflow:"hidden" put a
              position:sticky element inside an overflow:hidden ancestor,
              which breaks its sticky containing block. */}
          <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
            <CustomFilter
              title="Working Hours Report"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              getDetailedHeaders={getWorkingHoursExportHeaders}
              customDownloadHandler={handleWorkingHoursDownload}
              downloadFormats={["excel", "pdf", "csv"]}
              data={workingHoursRows}
              filteredData={wrkdHrsData}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={check_access}
              showtitle_count={true}
              hideCount={true}
              newButtonComponent={newButtonComponent}
              includeMetadata={true}
              metadata={getReportMetadata()}
              labeledToolbar={true}
              searchPlaceholder="Search by employee, department..."
            />
          </Box>
          <Box sx={{ ...designShell.sectionCard, mt: 0.5 }}>
            <Mui.Grid sx={{ pb: 0 }}>
              <WorkingHoursReport
                setShowReport={setShowReport}
                startDate={startDate}
                endDate={endDate}
                setWorkingHoursDetails={setWorkingHoursDetails}
                setMasterLeaveDataForSearch={setMasterLeaveDataForSearch}
                masterLeaveDataForSearch={masterLeaveDataForSearch}
                workingHoursDetails={workingHoursDetails}
                filteredWorkforceData={wrkdHrsData}
                fetchDetails={fetchDetails}
                setFetchDetails={setFetchDetails}
                departmentId={departmentId}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                page={page}
                setPage={setPage}
                hoursViewType={hoursViewType}
                header1={visibleHeader1}
                header2={visibleHeader2}
                customFilterHeight={customFilterHeight}
              />
            </Mui.Grid>
          </Box>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
